// @flow
import { parse } from "date-fns"
import { includes, inc } from "ramda"

import type {
  BoardingPass,
  CheckIn,
  CheckInStatus,
  PassengerTotals,
} from "../../../components/BookingCheckIn/types/internal"
import type {
  CheckInInbound,
  CheckInSegmentInbound,
  BoardingPassInbound,
} from "../../../components/BookingCheckIn/types/inbound"
import CHECKIN_STATUSES from "../../../components/BookingCheckIn/constants"

const { DONE, PROVIDE_DETAILS, WAITING_FOR_DETAILS, MISSED } = CHECKIN_STATUSES

const TOTAL_DEFAULTS: PassengerTotals = {
  totalPassengers: 0,
  totalBoardingPassesReady: 0,
  totalMissingDetails: 0,
}

const isBoardingPassReady = (status: CheckInStatus) => status === DONE
const isMissingDetails = (status: CheckInStatus) =>
  includes(status, [PROVIDE_DETAILS, WAITING_FOR_DETAILS, MISSED])

const reducePassengers = (
  { totalPassengers, totalBoardingPassesReady, totalMissingDetails }: PassengerTotals,
  { status }: { status: CheckInStatus },
): PassengerTotals => ({
  totalPassengers: inc(totalPassengers),
  totalBoardingPassesReady: isBoardingPassReady(status)
    ? inc(totalBoardingPassesReady)
    : totalBoardingPassesReady,
  totalMissingDetails: isMissingDetails(status) ? inc(totalMissingDetails) : totalMissingDetails,
})

export const calculatePassengerStateTotals = ({
  passengers,
}: CheckInSegmentInbound): PassengerTotals => passengers.reduce(reducePassengers, TOTAL_DEFAULTS)

const mapperBoardingPasses = (boardingPasses: ?Array<BoardingPassInbound>): Array<BoardingPass> =>
  boardingPasses
    ? boardingPasses.map(boardingPass => ({
        fileName: boardingPass.file_name,
        fileUrl: boardingPass.file_url,
        type: boardingPass.type,
      }))
    : []

const mapperCheckIn = (apiData: CheckInInbound): ?CheckIn => {
  if (!apiData) {
    return null
  }
  return {
    mergedBoardingPasses: mapperBoardingPasses(apiData.merged_boarding_passes),
    segments:
      apiData.segments &&
      apiData.segments.map(segment => ({
        status: segment.status,
        openDate: parse(segment.open_at),
        closeDate: parse(segment.close_at),
        passportDeadlineDate: parse(segment.passport_deadline_at),
        segmentCode: segment.segment_code,
        passengers:
          segment.passengers &&
          segment.passengers.map(passenger => ({
            id: passenger.passenger_id,
            status: passenger.status,
            boardingPasses: mapperBoardingPasses(passenger.boarding_passes),
            // airport fee not mapped as unused
          })),
        mergedBoardingPasses: mapperBoardingPasses(segment.merged_boarding_passes),
        ...calculatePassengerStateTotals(segment),
      })),
  }
}

export default mapperCheckIn
